import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePublicSettings } from "@/features/catalog/hooks";
import { trackPageView } from "@/lib/analytics";

/** Sadece güvenli tracking ID formatları (XSS enjeksiyonunu engeller) */
function sanitizeClientTrackingId(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^(AW|G|GTM)-[A-Za-z0-9.-]+$/.test(s)) return s;
  if (/^\d{5,20}$/.test(s)) return s;
  return null;
}

const INTERACTION_EVENTS = ["scroll", "click", "mousemove", "touchstart", "keydown"] as const;
const IDLE_DELAY_MS = 2500;

function injectGtag(googleId: string) {
  const scriptId = "google-ads-gtag-script";
  if (document.getElementById(scriptId)) return;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  window.gtag("js", new Date());
  window.gtag("config", googleId, { send_page_view: false });

  const scriptEl = document.createElement("script");
  scriptEl.id = scriptId;
  scriptEl.async = true;
  scriptEl.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleId)}`;
  document.head.appendChild(scriptEl);
}

function injectGtm(containerId: string) {
  const scriptId = "gtm-script";
  if (document.getElementById(scriptId)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const scriptEl = document.createElement("script");
  scriptEl.id = scriptId;
  scriptEl.async = true;
  scriptEl.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(scriptEl);
}

function injectMetaPixel(metaPixelId: string) {
  const scriptId = "meta-pixel-script";
  if (document.getElementById(scriptId)) return;

  const scriptEl = document.createElement("script");
  scriptEl.id = scriptId;
  scriptEl.textContent = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', ${JSON.stringify(metaPixelId)});
    fbq('track', 'PageView');
  `;
  document.head.appendChild(scriptEl);

  const noscriptId = "meta-pixel-noscript";
  if (!document.getElementById(noscriptId)) {
    const noscriptEl = document.createElement("noscript");
    noscriptEl.id = noscriptId;
    const img = document.createElement("img");
    img.height = 1;
    img.width = 1;
    img.style.display = "none";
    img.alt = "";
    img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(metaPixelId)}&ev=PageView&noscript=1`;
    noscriptEl.appendChild(img);
    document.body.appendChild(noscriptEl);
  }
}

function injectGoogle(googleId: string) {
  if (googleId.startsWith("GTM-")) injectGtm(googleId);
  else injectGtag(googleId);
}

/**
 * GTM / gtag / Meta Pixel: ilk boyamada yüklenmez.
 * İlk kullanıcı etkileşiminde veya 2.5 sn sonra enjekte edilir (LCP/TBT).
 */
export function useTrackingScripts() {
  const { data: settings } = usePublicSettings();

  useEffect(() => {
    const googleId = sanitizeClientTrackingId(settings?.tracking_google_ads_id);
    const metaPixelId = sanitizeClientTrackingId(settings?.tracking_meta_pixel_id);
    if (!googleId && !metaPixelId) return;

    let injected = false;
    const inject = () => {
      if (injected) return;
      injected = true;
      if (googleId) injectGoogle(googleId);
      if (metaPixelId) injectMetaPixel(metaPixelId);
    };

    const timeoutId = window.setTimeout(inject, IDLE_DELAY_MS);
    const onInteract = () => inject();
    INTERACTION_EVENTS.forEach((event) => {
      window.addEventListener(event, onInteract, { once: true, passive: true });
    });

    return () => {
      window.clearTimeout(timeoutId);
      INTERACTION_EVENTS.forEach((event) => {
        window.removeEventListener(event, onInteract);
      });
    };
  }, [settings?.tracking_google_ads_id, settings?.tracking_meta_pixel_id]);
}

/** SPA route değişiminde GTM dataLayer page_view. */
export function useSpaPageViews() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    if (lastPath.current === path) return;
    lastPath.current = path;
    trackPageView(path);
  }, [location.pathname, location.search]);
}
